<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { dynamicTime } from 'shared/helpers/timeHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import { useContactProfileIdentityLock } from 'dashboard/composables/useContactProfileIdentityLock';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  lastActivityAt: { type: [Number, String, Date], default: '' },
  createdAt: { type: [Number, String, Date], default: '' },
  responsibleAgentId: { type: [Number, String], default: null },
  socialProfiles: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'toggle',
  'updateContact',
  'showContact',
  'select',
  'avatarHover',
]);

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getVerifiedAgents');
const { isContactIdentityLockedForCurrentUser, canEditContactProfile } =
  useContactProfileIdentityLock();

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.code] = country;
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const responsibleAgent = computed(() => {
  const id = Number(props.responsibleAgentId);
  if (!id || !agents.value?.length) return null;
  return agents.value.find(agent => agent.id === id);
});

const lastActivityLabel = computed(() => {
  if (!props.lastActivityAt) return '';
  return dynamicTime(props.lastActivityAt);
});

const createdAtLabel = computed(() => {
  if (!props.createdAt) return '';
  return dynamicTime(props.createdAt);
});

const socialLinks = computed(() => {
  const profiles = props.socialProfiles || {};
  const map = {
    facebook: { icon: 'brand-facebook', prefix: 'https://facebook.com/' },
    twitter: { icon: 'brand-twitter', prefix: 'https://twitter.com/' },
    linkedin: { icon: 'brand-linkedin', prefix: 'https://linkedin.com/' },
    github: { icon: 'brand-github', prefix: 'https://github.com/' },
    instagram: { icon: 'brand-instagram', prefix: 'https://instagram.com/' },
    telegram: { icon: 'brand-telegram', prefix: 'https://t.me/' },
    tiktok: { icon: 'brand-tiktok', prefix: 'https://tiktok.com/@' },
    line: { icon: 'brand-line', prefix: 'https://line.me/R/ti/p/~' },
    phone: { icon: 'brand-whatsapp', prefix: 'https://wa.me/' },
    website: { icon: 'globe', prefix: 'https://' },
  };

  return Object.entries(profiles)
    .filter(([, value]) => !!value)
    .map(([key, value]) => {
      const { icon, prefix } = map[key] || {
        icon: `brand-${key}`,
        prefix: 'https://',
      };
      return {
        key,
        icon,
        href: `${prefix}${value}`,
      };
    });
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);

const toggleSelect = checked => {
  emit('select', checked);
};

const handleAvatarHover = isHovered => {
  emit('avatarHover', isHovered);
};

onMounted(() => {
  store.dispatch('agents/get');
});
</script>

<template>
  <div class="relative">
    <CardLayout
      :key="id"
      layout="row"
      :class="{
        'outline-n-weak !bg-n-slate-3 dark:!bg-n-solid-3': isSelected,
      }"
    >
      <div class="flex items-center justify-start flex-1 gap-4">
        <div
          class="relative"
          @mouseenter="handleAvatarHover(true)"
          @mouseleave="handleAvatarHover(false)"
        >
          <Avatar
            :name="name"
            :src="thumbnail"
            :size="48"
            :status="availabilityStatus"
            hide-offline-status
            rounded-full
          >
            <template v-if="selectable" #overlay="{ size }">
              <label
                class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px] border border-n-weak"
                :style="{ width: `${size}px`, height: `${size}px` }"
                @click.stop
              >
                <Checkbox
                  :model-value="isSelected"
                  @change="event => toggleSelect(event.target.checked)"
                />
              </label>
            </template>
          </Avatar>
        </div>
        <div class="flex flex-col gap-0.5 flex-1">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span class="text-base font-medium truncate text-n-slate-12">
              {{ name }}
            </span>
            <span class="inline-flex items-center gap-1">
              <span
                v-if="additionalAttributes?.companyName"
                class="i-ph-building-light size-4 text-n-slate-10 mb-0.5"
              />
              <span
                v-if="additionalAttributes?.companyName"
                class="text-sm truncate text-n-slate-11"
              >
                {{ additionalAttributes.companyName }}
              </span>
            </span>
          </div>
          <div
            class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1"
          >
            <div v-if="email" class="truncate max-w-72" :title="email">
              <span class="text-sm text-n-slate-11">
                {{ email }}
              </span>
            </div>
            <div v-if="email" class="w-px h-3 truncate bg-n-slate-6" />
            <span v-if="phoneNumber" class="text-sm truncate text-n-slate-11">
              {{ phoneNumber }}
            </span>
            <div v-if="phoneNumber" class="w-px h-3 truncate bg-n-slate-6" />
            <span
              v-if="countryDetails"
              class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
            >
              <Flag :country="countryDetails.countryCode" class="size-3.5" />
              {{ formattedLocation }}
            </span>
            <div v-if="countryDetails" class="w-px h-3 truncate bg-n-slate-6" />
            <Button
              :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
              variant="link"
              size="xs"
              @click="onClickViewDetails"
            />
          </div>
          <div
            v-if="
              responsibleAgent ||
              lastActivityLabel ||
              createdAtLabel ||
              socialLinks.length
            "
            class="flex flex-wrap items-center gap-3 text-xs text-n-slate-11"
          >
            <span
              v-if="responsibleAgent"
              class="inline-flex items-center gap-1"
            >
              <span class="i-ph-user-circle text-n-slate-10 size-4" />
              {{ responsibleAgent.name }}
            </span>
            <span
              v-if="lastActivityLabel"
              class="inline-flex items-center gap-1"
            >
              <span class="i-ph-clock-afternoon text-n-slate-10 size-4" />
              {{ lastActivityLabel }}
            </span>
            <span
              v-if="createdAtLabel"
              class="inline-flex items-center gap-1"
            >
              <span class="i-ph-calendar-blank text-n-slate-10 size-4" />
              {{ createdAtLabel }}
            </span>
            <div
              v-if="socialLinks.length"
              class="inline-flex items-center gap-2 text-n-slate-10"
            >
              <a
                v-for="item in socialLinks"
                :key="item.key"
                :href="item.href"
                target="_blank"
                rel="noopener noreferrer nofollow"
                class="hover:text-n-slate-12"
              >
                <fluent-icon :icon="item.icon" size="14" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <Button
        v-if="canEditContactProfile"
        icon="i-lucide-chevron-down"
        variant="ghost"
        color="slate"
        size="xs"
        :class="{ 'rotate-180': isExpanded }"
        @click="onClickExpand"
      />

      <template v-if="canEditContactProfile" #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <div class="flex flex-col gap-6 p-6 border-t border-n-strong">
              <ContactsForm
                ref="contactsFormRef"
                :contact-data="contactData"
                :lock-identity-fields="isContactIdentityLockedForCurrentUser"
                @update="handleFormUpdate"
              />
              <div>
                <Button
                  :label="
                    t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                  "
                  size="sm"
                  :is-loading="isUpdating"
                  :disabled="isUpdating || isFormInvalid"
                  @click="handleUpdateContact"
                />
              </div>
            </div>
            <ContactDeleteSection
              :selected-contact="{
                id: props.id,
                name: props.name,
              }"
            />
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
